/**
 * `rehype-harden`, the last plugin in Streamdown's rehype pipeline, drops any anchor whose href it
 * cannot parse: a relative target parses only when it starts with `/`, `./` or `../`, so the bare
 * form the platform prompt prescribes rendered as inert "[blocked]" text (#6659). Respelling the
 * href is all this module does. `rehype-sanitize`, upstream of it, is what strips a dangerous
 * scheme, and harden still gates every href this module rewrites.
 *
 * The module now holds two things, both about which link targets the chat will render. The
 * respelling above gets a bare relative path THROUGH harden. {@link isProtocolRelativeHref} is the
 * opposite: the one shape harden lets through that it should not, a target that resolves to a host
 * instead of a path. Both hosts' anchors call it first.
 */

/** True for a `scheme:` URL, a protocol-relative `//host` or a `#fragment`; false for a path. */
export const isExternalHref = (href?: string): boolean =>
    !href || /^([a-z][a-z0-9+.-]*:|\/\/|#)/i.test(href)

/**
 * What the URL parser sees. It removes every ASCII tab, newline and carriage return from the whole
 * input, then ignores leading and trailing C0 controls and spaces. So `/<tab>/host` is `//host` to a
 * browser, and this has to be the shape the checks below run on.
 */
const asBrowserReadsIt = (value: string): string => {
    const stripped = value.replace(/[\t\n\r]/g, "")
    let start = 0
    let end = stripped.length
    while (start < end && stripped[start] <= " ") start += 1
    while (end > start && stripped[end - 1] <= " ") end -= 1
    return stripped.slice(start, end)
}

/** Resolving against this tells a same-document path apart from one that names another host. */
const PROBE_ORIGIN = "http://link-gate.invalid"
const PROBE_HOST = "link-gate.invalid"

/**
 * True when a browser would read this href as naming a HOST rather than a path in this app (#6666).
 *
 * The escape harden leaves open: it parses a target that starts with `../`, then emits
 * `parsedUrl.pathname`, and the pathname of `..//evil.com/x` is `//evil.com/x`. That is a
 * protocol-relative URL, so the browser fills in the page's scheme and navigates off-site under a
 * link text the markdown author chose. Model output is markdown an agent can be steered into
 * writing, so the anchor refuses the shape instead of trusting the target.
 *
 * Two normalizations are what a browser really does, and the check has to match them or it can be
 * spelled around. It removes every ASCII tab, newline and carriage return from the whole href and
 * ignores the surrounding C0 controls and spaces, so `/<tab>/host` is `//host` to it
 * ({@link asBrowserReadsIt}). And it reads a backslash as a slash in an http(s) URL, so `/\host`,
 * `\/host` and `\\host` are all `//host`.
 *
 * The percent-decoding rounds are NOT that. A browser leaves `%2F` encoded, so `%2F%2Fhost` is a
 * same-origin path to it. They are here because a target that decodes to a host is never a link
 * this chat wants to render, and because {@link decodeDriveHref} downstream does decode one layer.
 * Four rounds is a bounded belt-and-braces limit, not a model of anything; a deeper encoding is
 * still just an encoded path. The cost is refusing a filename that literally contains `%2F%2F`,
 * which is why {@link fileCandidate} uses the literal prefix test instead of this one.
 *
 * The last step resolves what is left against a probe origin, which catches a target harden did not
 * resolve. `link-gate.invalid` is a reserved TLD, so nothing can name it on purpose, and every
 * host-naming spelling already opens with `//` after the normalizations above.
 *
 * A `scheme:` URL is not this function's business and returns false: `rehype-sanitize` drops a
 * dangerous scheme and `rehype-harden` gates the rest, and an `https://` link is a link a reply is
 * allowed to contain. That also keeps `https://example.com//deep/path` working, whose own pathname
 * starts with `//`. The cost of the rule is a legitimate `//host` spelling of a web link in a
 * reply, which renders blocked. That trade is the decision recorded on #6666.
 */
export const isProtocolRelativeHref = (href?: string | null): boolean => {
    if (typeof href !== "string" || !href) return false
    let value = href
    // The loop stops as soon as decoding is a no-op, so an ordinary path costs one pass.
    for (let round = 0; round < 4; round += 1) {
        value = asBrowserReadsIt(value)
        if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return false
        if (value.replace(/\\/g, "/").startsWith("//")) return true
        let decoded: string
        try {
            decoded = decodeURIComponent(value)
        } catch {
            break
        }
        if (decoded === value) break
        value = decoded
    }
    try {
        const url = new URL(value.replace(/\\/g, "/"), PROBE_ORIGIN)
        // A different host means the target named one. A pathname that still starts with `//` is
        // the shape harden hands back, which the NEXT resolution (the browser's) reads as a host.
        return url.host !== PROBE_HOST || url.pathname.startsWith("//")
    } catch {
        return false
    }
}

/** The explicit-relative spelling of a bare relative href, else null. */
export const explicitRelativeHref = (href: string): string | null => {
    if (!href || isExternalHref(href)) return null
    if (href.startsWith("/") || href.startsWith("./") || href.startsWith("../")) return null
    const explicit = `./${href}`
    try {
        // A dot segment can climb to `//host`, which harden hands back as an OFF-SITE link.
        if (new URL(explicit, "http://x.invalid").pathname.startsWith("//")) return null
    } catch {
        return null
    }
    return explicit
}

/** Enough of a hast node to walk anchors without a `hast` dependency. */
interface HastNode {
    type: string
    tagName?: string
    properties?: Record<string, unknown> | null
    children?: HastNode[] | null
}

/** Respell bare relative anchor hrefs. MUST run immediately before `rehype-harden`. */
export const rehypeExplicitRelativeLinks = () => (tree: HastNode) => {
    const walk = (node: HastNode) => {
        if (node.type === "element" && node.tagName === "a" && node.properties) {
            const href = node.properties.href
            if (typeof href === "string") {
                const explicit = explicitRelativeHref(href)
                if (explicit) node.properties.href = explicit
            }
        }
        const children = node.children
        if (Array.isArray(children)) for (const child of children) if (child) walk(child)
    }
    walk(tree)
    return tree
}

/** Harden's own class for a blocked link — the refused image wears the same one. */
const BLOCKED_CLASS = "text-gray-500"

/**
 * Refuse an image whose target names a host the reader cannot see (#6680).
 *
 * The same escape as the anchor's (#6666), on a different sink: harden parses `..//evil.com/x.png`
 * and emits the pathname `//evil.com/x.png`, and Streamdown's image renderer then fetches it and
 * offers a download button whose fallback is `window.open` on the raw target — a click on
 * "download this image" that lands on another site. The anchor gate cannot help here because the
 * image renderer is Streamdown's own, so the refusal happens in the tree, before harden and
 * before any renderer sees the node: the image becomes the span harden itself leaves for a link
 * it refuses, so a reader cannot tell which layer caught which.
 *
 * An explicit `https://` image is a different decision and stays allowed, exactly as a scheme
 * URL does for links — `isProtocolRelativeHref` returns false for it.
 */
export const rehypeRefuseHostImages = () => (tree: HastNode) => {
    const walk = (node: HastNode) => {
        if (node.type === "element" && node.tagName === "img" && node.properties) {
            const src = node.properties.src
            if (typeof src === "string" && isProtocolRelativeHref(src)) {
                const alt = typeof node.properties.alt === "string" ? node.properties.alt : ""
                node.tagName = "span"
                node.properties = {title: `Blocked URL: ${src}`, className: [BLOCKED_CLASS]}
                node.children = [{type: "text", value: `${alt || "image"} [blocked]`} as HastNode]
            }
        }
        const children = node.children
        if (Array.isArray(children)) for (const child of children) if (child) walk(child)
    }
    walk(tree)
    return tree
}

/** Streamdown's own rehype list with the chat's gates inserted before its harden gate: the
 * anchor respelling, and the image refusal. */
export const withExplicitRelativeLinks = <T>(
    defaults: Record<string, T>,
): (T | typeof rehypeExplicitRelativeLinks | typeof rehypeRefuseHostImages)[] =>
    Object.entries(defaults).flatMap(([name, plugin]) =>
        name === "harden"
            ? [rehypeExplicitRelativeLinks, rehypeRefuseHostImages, plugin]
            : [plugin],
    )

/** Undo the percent-encoding harden's `new URL()` round-trip adds; drive paths are raw. */
export const decodeDriveHref = (href: string): string => {
    try {
        return decodeURIComponent(href)
    } catch {
        return href
    }
}
